import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@services/api';
import type { PlayerCardDistribution, PlayerCardDistributionDivision } from '@/types';
import { apiErrorMessage, apiErrorStatus } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import {
  CARD_VARIANTS,
  fmtCount,
  normaliseDistribution,
  shareOf,
  sortedSources,
  sourceColumnsOf,
} from '@/utils/cardDistribution';

const ALL_VARIANTS = 'all';
type VariantChoice = number | typeof ALL_VARIANTS;

const DISTRIBUTION_HELP =
  'Owners: gamers who own at least one card of this player now (any source), by the division they are in ' +
  'today (no division row = division 1). Cards: his cards those gamers hold. Bought IAP / coins: cards traced ' +
  'to a real-money or coin purchase; Earned: the rest. A card variant counts only cards of that variant. ' +
  'Read from the admin read replica when you open this section.';

const SOURCE_HELP =
  'Recorded on each new card from the date shown: how the card was made and the owner’s division at that ' +
  'moment. Cards made earlier have no source and are not counted here.';

const formatIst = (iso: string) =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })} IST`;

const countCell = (value: number) => fmtCount(value);

export const CardDistributionSection: React.FC<{ playerIndex: number }> = ({ playerIndex }) => {
  const [variant, setVariant] = useState<VariantChoice>(ALL_VARIANTS);
  const [data, setData] = useState<PlayerCardDistribution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const guard = useRef(createLatestGuard());

  const load = useCallback(async () => {
    const isLatest = guard.current.begin();
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getPlayerCardDistribution(
        playerIndex,
        variant === ALL_VARIANTS ? undefined : variant
      );
      if (!isLatest()) return;
      setData(normaliseDistribution(res.data));
    } catch (err) {
      if (!isLatest()) return;
      setData(null);
      setError(
        apiErrorStatus(err) === 404
          ? 'This server does not have the card distribution yet.'
          : apiErrorMessage(err, 'Could not load the card distribution.')
      );
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [playerIndex, variant]);

  useEffect(() => {
    void load();
  }, [load]);

  const divisionNames = new Map(
    (data?.ownersByDivision ?? []).map((d) => [d.tier, `${d.tier}. ${d.name}`])
  );
  const sources = sortedSources(data?.cardsBySource);
  const trackedTotal = sources.reduce((sum, s) => sum + s.count, 0);
  const sourceColumns = sourceColumnsOf(data?.cardsBySourceDivision ?? []);
  const sourceDivisionRows = (data?.cardsBySourceDivision ?? []).filter((row) =>
    Object.values(row.counts).some((n) => n > 0)
  );

  return (
    <Card
      title={
        <Space size={8}>
          Card distribution
          <Tooltip title={DISTRIBUTION_HELP}>
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Space size={8} wrap>
          <Segmented<VariantChoice>
            size="small"
            value={variant}
            onChange={setVariant}
            options={[
              { label: 'All', value: ALL_VARIANTS },
              ...CARD_VARIANTS.map((label, rarity) => ({ label, value: rarity })),
            ]}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => void load()}
          />
        </Space>
      }
      style={{ marginTop: 20 }}
      size="small"
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Spin spinning={loading}>
        {data ? (
          <>
            <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
              <Col xs={12} sm={8} md={4}>
                <Statistic title="Owners" value={fmtCount(data.owners)} />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic title="Cards" value={fmtCount(data.cards)} />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Bought IAP"
                  value={fmtCount(data.boughtIap)}
                  suffix={
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {shareOf(data.boughtIap, data.cards) ?? ''}
                    </Typography.Text>
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Bought coins"
                  value={fmtCount(data.boughtCoins)}
                  suffix={
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {shareOf(data.boughtCoins, data.cards) ?? ''}
                    </Typography.Text>
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Earned"
                  value={fmtCount(data.earned)}
                  suffix={
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {shareOf(data.earned, data.cards) ?? ''}
                    </Typography.Text>
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography.Text type="secondary">
                  {data.computedAt ? `Read ${formatIst(data.computedAt)}` : ''}
                </Typography.Text>
              </Col>
            </Row>

            <Typography.Title level={5} style={{ fontSize: 14 }}>
              Owners by division
            </Typography.Title>
            <Table<PlayerCardDistributionDivision>
              dataSource={data.ownersByDivision}
              rowKey="tier"
              size="small"
              pagination={false}
              scroll={{ x: 720 }}
              locale={{ emptyText: 'No division rows in the response' }}
              columns={[
                {
                  title: 'Division',
                  key: 'division',
                  render: (_, d) => `${d.tier}. ${d.name}`,
                },
                { title: 'Owners', dataIndex: 'owners', align: 'right', render: countCell },
                {
                  title: '% of owners',
                  key: 'share',
                  align: 'right',
                  render: (_, d) => shareOf(d.owners, data.owners) ?? '—',
                },
                { title: 'Cards', dataIndex: 'cards', align: 'right', render: countCell },
                {
                  title: 'Bought IAP',
                  dataIndex: 'boughtIap',
                  align: 'right',
                  render: countCell,
                },
                {
                  title: 'Bought coins',
                  dataIndex: 'boughtCoins',
                  align: 'right',
                  render: countCell,
                },
              ]}
            />

            <Typography.Title level={5} style={{ fontSize: 14, marginTop: 20 }}>
              <Space size={8}>
                {data.sourceTrackedSince
                  ? `Source of new cards (since ${formatIst(data.sourceTrackedSince)})`
                  : 'Source of new cards'}
                <Tooltip title={SOURCE_HELP}>
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            </Typography.Title>
            {sources.length === 0 ? (
              <Typography.Text type="secondary">
                {data.sourceTrackedSince
                  ? 'No new cards of this player with a recorded source yet.'
                  : 'Card sources are not recorded yet, so there is nothing to show.'}
              </Typography.Text>
            ) : (
              <>
                <Table
                  dataSource={sources}
                  rowKey="source"
                  size="small"
                  pagination={false}
                  style={{ maxWidth: 520 }}
                  columns={[
                    { title: 'Source', dataIndex: 'label' },
                    { title: 'Cards', dataIndex: 'count', align: 'right', render: countCell },
                    {
                      title: '% of tracked',
                      key: 'share',
                      align: 'right',
                      render: (_, s) => shareOf(s.count, trackedTotal) ?? '—',
                    },
                  ]}
                />
                <Collapse
                  size="small"
                  style={{ marginTop: 12 }}
                  items={[
                    {
                      key: 'by-division',
                      label: 'By division (the owner’s division when the card was made)',
                      children:
                        sourceDivisionRows.length === 0 ? (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No division breakdown in the response"
                          />
                        ) : (
                          <Table
                            dataSource={sourceDivisionRows}
                            rowKey="tier"
                            size="small"
                            pagination={false}
                            scroll={{ x: 200 + sourceColumns.length * 120 }}
                            columns={[
                              {
                                title: 'Division',
                                key: 'division',
                                fixed: 'left',
                                width: 200,
                                render: (_, row) =>
                                  divisionNames.get(row.tier) ?? `Division ${row.tier}`,
                              },
                              ...sourceColumns.map((s) => ({
                                title: s.label,
                                key: s.source,
                                align: 'right' as const,
                                render: (_: unknown, row: { counts: Record<string, number> }) =>
                                  fmtCount(row.counts[s.source] ?? 0),
                              })),
                            ]}
                          />
                        ),
                    },
                  ]}
                />
              </>
            )}
          </>
        ) : (
          !loading && !error && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        )}
      </Spin>
    </Card>
  );
};
